import axios, { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { message } from 'antd';
import { isDevelopment } from '../utils/config';

// 扩展 Axios 配置类型以支持 metadata 属性
declare module 'axios' {
 interface InternalAxiosRequestConfig {
   metadata?: {
     startTime?: number;
     endTime?: number;
   };
 }
}

// 扩展 AxiosError，携带后端可读错误信息
declare module 'axios' {
  interface AxiosError {
    displayMessage?: string;
  }
}

const API_URL = process.env.REACT_APP_API_URL || '';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to add auth token and record start time
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 记录请求开始时间（用于计算耗时）
    config.metadata = {
      ...config.metadata,
      startTime: Date.now()
    };

    // 开发环境下打印请求信息
    if (isDevelopment()) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`, {
        headers: config.headers,
        data: config.data,
        params: config.params,
        timestamp: new Date().toISOString()
      });
    }

    const authStateString = localStorage.getItem('authState');
    if (authStateString) {
      try {
        const authState = JSON.parse(authStateString);
        if (authState.token) {
          config.headers.Authorization = `Bearer ${authState.token}`;
        } else {
          console.log('No token found in authState');
        }
      } catch (error) {
        console.error('Error parsing authState:', error);
      }
    } else {
      console.log('No authState found in localStorage');
    }
    return config;
  },
  (error) => {
    // 开发环境下打印请求错误
    if (isDevelopment()) {
      console.error('[API Request Error]', error);
    }
    // 提取后端返回的错误信息，挂载到 error 上方便上层直接使用
    const backendMessage =
      error.response?.data?.message ||
      error.response?.data?.title ||
      (typeof error.response?.data === 'string' ? error.response.data : null);
    if (backendMessage) {
      error.displayMessage = backendMessage;
    }
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // 记录响应结束时间
    response.config.metadata = {
      ...response.config.metadata,
      endTime: Date.now()
    };

    // 开发环境下打印成功响应信息
    if (isDevelopment()) {
      const startTime = response.config.metadata?.startTime;
      const endTime = response.config.metadata?.endTime;
      const duration = startTime && endTime ? 
        (endTime - startTime) + 'ms' : 'N/A';
      
      console.log(`[API Response Success] ${response.config.method?.toUpperCase()} ${response.config.baseURL}${response.config.url}`, {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        headers: response.headers,
        timestamp: new Date().toISOString(),
        duration: duration
      });
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig;

    // 记录响应结束时间（如果有请求配置）
    if (originalRequest) {
      originalRequest.metadata = {
        ...originalRequest.metadata,
        endTime: Date.now()
      };
    }

    // 开发环境下打印错误响应信息
    if (isDevelopment()) {
      const startTime = originalRequest?.metadata?.startTime;
      const endTime = originalRequest?.metadata?.endTime;
      const duration = startTime && endTime ? 
        (endTime - startTime) + 'ms' : 'N/A';
      
      console.error(`[API Response Error] ${originalRequest?.method?.toUpperCase()} ${originalRequest?.baseURL}${originalRequest?.url}`, {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
        error: error.message,
        timestamp: new Date().toISOString(),
        duration: duration
      });
    }

    // 检查是否是登录请求或刷新token请求
    const isLoginRequest = originalRequest?.url?.includes('/auth/login');
    const isRefreshRequest = originalRequest?.url?.includes('/auth/refresh');

    if (error.response?.status === 401) {
      // 只有非登录请求和非刷新token请求的 401 错误才处理
      if (!isLoginRequest && !isRefreshRequest) {
        console.error('Unauthorized access:', {
          status: error.response.status,
          message: error.response.data?.message || 'No error message provided',
          url: originalRequest?.url,
          method: originalRequest?.method,
          headers: originalRequest?.headers
        });

        // 尝试刷新token
        const authStateString = localStorage.getItem('authState');
        if (authStateString) {
          try {
            const authState = JSON.parse(authStateString);
            if (authState.token) {
              // 调用刷新token接口
              const refreshResponse = await api.post('/auth/refresh', { token: authState.token });
              if (refreshResponse.status === 200) {
                const { token: newToken, expiresAt } = refreshResponse.data;

                // 更新本地存储的token
                const updatedAuthState = {
                  ...authState,
                  token: newToken
                };
                localStorage.setItem('authState', JSON.stringify(updatedAuthState));

                // 更新token过期时间
                if (expiresAt) {
                  localStorage.setItem('tokenExpiresAt', expiresAt);
                }

                // 更新请求头并重试原始请求
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return api(originalRequest);
              }
            }
          } catch (refreshError: any) {
            console.error('Token refresh failed:', {
              status: refreshError.response?.status,
              message: refreshError.response?.data?.message || 'Token refresh failed',
              error: refreshError.message
            });

            // 刷新token失败，直接跳转到登录页
            localStorage.removeItem('authState');
            localStorage.removeItem('tokenExpiresAt');
            localStorage.removeItem('userName');
            message.error('登录已过期，请重新登录');
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }
      }
    } else if (error.response?.status === 404) {
      // Handle not found error
      console.error('API endpoint not found:', {
        status: error.response.status,
        message: error.response.data?.message || 'API endpoint not found',
        url: originalRequest?.url,
        method: originalRequest?.method
      });
      // 显示友好的错误消息
      message.error('服务器连接失败，请检查网络连接或联系管理员');
    }
    return Promise.reject(error);
  }
);

export default api;
